import type { TThrownError } from '@/services/errors';
import type { TStaffInterview } from '@/services/interviews';
import type {
  TStaffAvailability,
  TStaffDirectoryEntry,
  TStaffHandover,
  TStaffHoldKind,
  TStaffTransitionAvailability,
  TStaffTransitionBlocker,
  TStaffTransitionCandidate,
  TStaffTransitionIntent,
  TStaffTransitionMove,
  TStaffTransitionPayload,
  TStaffTransitionReport,
} from '@/services/staff';

import {
  HOLD_KIND_COPY,
  STAFF_TRANSITION_COPY,
  TRANSITION_BLOCKER_COPY,
  TRANSITION_ERROR_COPY,
  TRANSITION_KIND_TOKEN,
} from '@/constants/adminManage';
import { ERROR_FALLBACK } from '@/constants/errors';
import { ROUND_ROBIN } from '@/services/staff';
import { toRoleLabel } from '@/utils/adminManage';
import { resolveErrorCopy } from '@/utils/errorCopy';
import { toStaffDisplayName } from '@/utils/staffName';

import type { TSelectOption } from 'components/ui/Select';

const INTERVIEWS_PENDING_CODE = 'interviews_pending';

const HANDOVER_MISSING_CODE = 'handover_missing';

const toTransitionPayload = (
  intent: TStaffTransitionIntent,
  handover: TStaffHandover,
  isDryRun: boolean,
): TStaffTransitionPayload => ({ ...intent, handover, isDryRun });

const isTransitionClear = (report: TStaffTransitionReport): boolean => report.blockers.length === 0;

const isCrossingInactive = (from: TStaffAvailability, to: TStaffAvailability): boolean => (from === 'inactive') !== (to === 'inactive');

const toTransitionAvailability = (availability: TStaffAvailability): TStaffTransitionAvailability => {
  if (availability === 'inactive') {
    return 'inactive';
  }

  return 'active';
};

const toTransitionTitle = (intent: TStaffTransitionIntent | null): string => {
  if (intent?.availability === 'inactive') {
    return STAFF_TRANSITION_COPY.deactivateTitle;
  }

  if (intent?.availability === 'active') {
    return STAFF_TRANSITION_COPY.reactivateTitle;
  }

  return STAFF_TRANSITION_COPY.roleTitle;
};

const toHoldLabel = (kind: TStaffHoldKind, count: number): string => {
  const copy = HOLD_KIND_COPY[kind];

  if (count === 1) {
    return `${count} ${copy.one}`;
  }

  return `${count} ${copy.many}`;
};

const toDirectoryNames = (entries: readonly TStaffDirectoryEntry[] = []): ReadonlyMap<string, string> => new Map(
  entries.map(entry => [entry.userID, toStaffDisplayName(entry.firstName, entry.lastName)]),
);

const toHandoverOptions = (
  candidates: readonly TStaffTransitionCandidate[],
  names: ReadonlyMap<string, string>,
): TSelectOption[] => [
  { value: ROUND_ROBIN, label: STAFF_TRANSITION_COPY.roundRobin },
  ...candidates.map(candidate => ({
    value: candidate.staffID,
    label: `${names.get(candidate.staffID) ?? candidate.staffID} · ${toRoleLabel(candidate.subRole)}`,
  })),
];

const toVisibleBlockers = (blockers: readonly TStaffTransitionBlocker[]): TStaffTransitionBlocker[] => blockers
  .filter(blocker => blocker.code !== HANDOVER_MISSING_CODE);

const toBlockerKey = (blocker: TStaffTransitionBlocker): string => `${blocker.code}-${blocker.kind ?? 'all'}`;

const toBlockerCopy = (blocker: TStaffTransitionBlocker): string => {
  const copy = TRANSITION_BLOCKER_COPY[blocker.code] ?? ERROR_FALLBACK;

  if (blocker.kind === null) {
    return copy;
  }

  return copy.replace(TRANSITION_KIND_TOKEN, HOLD_KIND_COPY[blocker.kind].many);
};

const toPendingBookingIDs = (report: TStaffTransitionReport | null): string[] => report?.blockers
  .find(blocker => blocker.code === INTERVIEWS_PENDING_CODE)?.bookingIDs ?? [];

const toPendingCalls = (
  interviews: readonly TStaffInterview[] | undefined,
  bookingIDs: readonly string[],
): TStaffInterview[] => (interviews ?? []).filter(interview => bookingIDs.includes(interview.bookingID));

const toMoveKey = (move: TStaffTransitionMove): string => `${move.kind}-${move.staffID}`;

const toMoveLabel = (move: TStaffTransitionMove, names: ReadonlyMap<string, string>): string => (
  `${toHoldLabel(move.kind, move.count)} → ${names.get(move.staffID) ?? move.staffID}`
);

const toMemberHolderIDs = (report: TStaffTransitionReport): string[] => [...new Set(report.moved
  .filter(move => move.kind === 'member')
  .map(move => move.staffID))];

const toTransitionErrorCopy = (error: Error | null): string | null => {
  if (error === null) {
    return null;
  }

  const { reason } = error as TThrownError;

  return TRANSITION_ERROR_COPY[reason ?? error.message] ?? TRANSITION_ERROR_COPY[error.message] ?? resolveErrorCopy(error.message) ?? ERROR_FALLBACK;
};

export {
  isCrossingInactive,
  isTransitionClear,
  toBlockerCopy,
  toBlockerKey,
  toDirectoryNames,
  toHandoverOptions,
  toHoldLabel,
  toMemberHolderIDs,
  toMoveKey,
  toMoveLabel,
  toPendingBookingIDs,
  toPendingCalls,
  toTransitionAvailability,
  toTransitionErrorCopy,
  toTransitionPayload,
  toTransitionTitle,
  toVisibleBlockers,
};
